import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PiPaletteBold } from 'react-icons/pi';
import V2Modal from '@v2/components/ui/V2Modal';
import { Button } from '@v2/design-system';
import api from '@v2/lib/api';

export type ColorSlotKey = 'frame' | 'upholstery' | 'stitches' | 'arm';

export type ColorSlotDetail = {
    po_raw: string | null;
    pi_raw: string | null;
    resolved_po: string | null;
    resolved_pi: string | null;
    alias_matched_po?: boolean;
    alias_matched_pi?: boolean;
};

type ProductColorOption = {
    id: number;
    category: string;
    name: string;
    hex: string | null;
    active: boolean;
};

type ColorAliasRecord = {
    id: number;
    category: string;
    alias: string;
    product_color_id: number;
    created_by_user?: { id: number; name: string } | null;
    created_at?: string | null;
    product_color?: ProductColorOption;
};

const SLOT_LABELS: Record<ColorSlotKey, string> = {
    frame: 'Estrutura',
    upholstery: 'Estofado',
    stitches: 'Costura',
    arm: 'Braço',
};

/** Campo PO/PI → categoria do catálogo na API. */
function slotToCategory(slot: ColorSlotKey): string {
    return slot === 'upholstery' ? 'seat' : slot;
}

type Props = {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    slot: ColorSlotKey | null;
    slotDetail: ColorSlotDetail | null;
    productName?: string | null;
};

export default function PiCheckColorModal({
    open,
    onClose,
    onSaved,
    slot,
    slotDetail,
    productName,
}: Props) {
    const category = slot ? slotToCategory(slot) : 'frame';
    const [productColorId, setProductColorId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const poRaw = slotDetail?.po_raw?.trim() || '';
    const piRaw = slotDetail?.pi_raw?.trim() || '';
    const needsPoAlias = poRaw !== '' && poRaw.toLowerCase() !== piRaw.toLowerCase();
    const needsPiAlias = piRaw !== '' && piRaw.toLowerCase() !== poRaw.toLowerCase();

    const { data: colors = [], isLoading: colorsLoading } = useQuery({
        queryKey: ['meta-product-colors', category],
        queryFn: async () => {
            const { data } = await api.get<{ data: ProductColorOption[] }>('/meta/product-colors', {
                params: { category },
            });
            return data.data ?? [];
        },
        enabled: open && !!slot,
    });

    const aliasSearch = useMemo(() => {
        const terms = [poRaw, piRaw].filter(Boolean);
        return terms[0] ?? '';
    }, [poRaw, piRaw]);

    const { data: existingAliases = [] } = useQuery({
        queryKey: ['color-aliases', category, aliasSearch],
        queryFn: async () => {
            const { data } = await api.get<{ data: ColorAliasRecord[] }>('/color-aliases', {
                params: { category, search: aliasSearch || undefined },
            });
            return data.data ?? [];
        },
        enabled: open && !!slot && !!aliasSearch,
    });

    const existingForPo = existingAliases.find(
        (a) => a.alias.trim().toLowerCase() === poRaw.toLowerCase(),
    );
    const existingForPi = existingAliases.find(
        (a) => a.alias.trim().toLowerCase() === piRaw.toLowerCase(),
    );

    useEffect(() => {
        if (!open) return;
        setError(null);
        const resolved =
            slotDetail?.resolved_po?.trim() ||
            slotDetail?.resolved_pi?.trim() ||
            '';
        const match = colors.find(
            (c) => c.name.trim().toLowerCase() === resolved.toLowerCase(),
        );
        setProductColorId(
            match?.id ??
                existingForPo?.product_color_id ??
                existingForPi?.product_color_id ??
                null,
        );
    }, [open, slot, slotDetail, colors, existingForPo, existingForPi]);

    const saveMut = useMutation({
        mutationFn: async () => {
            if (!productColorId || !slot) {
                throw new Error('Selecione a cor interna do catálogo.');
            }
            const items: Array<{
                category: string;
                alias: string;
                product_color_id: number;
            }> = [];
            if (needsPoAlias && poRaw) {
                items.push({ category, alias: poRaw, product_color_id: productColorId });
            }
            if (needsPiAlias && piRaw) {
                items.push({ category, alias: piRaw, product_color_id: productColorId });
            }
            if (items.length === 0 && poRaw) {
                items.push({ category, alias: poRaw, product_color_id: productColorId });
            } else if (items.length === 0 && piRaw) {
                items.push({ category, alias: piRaw, product_color_id: productColorId });
            }
            if (items.length === 1) {
                await api.post('/color-aliases', items[0]);
            } else {
                await api.post('/color-aliases/batch', { items });
            }
        },
        onSuccess: () => {
            onSaved();
            onClose();
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message ?? 'Não foi possível salvar o vínculo de cor.';
            setError(msg);
        },
    });

    if (!slot || !slotDetail) {
        return null;
    }

    const auditUser =
        existingForPo?.created_by_user ?? existingForPi?.created_by_user ?? null;
    const auditAt = existingForPo?.created_at ?? existingForPi?.created_at ?? null;

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title="Vincular cor ao catálogo"
            size="md"
        >
            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    {productName ? (
                        <>
                            Equipamento: <strong>{productName}</strong>
                            <br />
                        </>
                    ) : null}
                    Categoria: <strong>{SLOT_LABELS[slot]}</strong>
                </p>

                <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                    <div>
                        <span className="text-xs font-semibold uppercase text-emerald-800">PO</span>
                        <p className="mt-1 font-medium text-gray-900">{poRaw || '—'}</p>
                        {slotDetail.resolved_po ? (
                            <p className="text-xs text-gray-500">→ {slotDetail.resolved_po}</p>
                        ) : null}
                    </div>
                    <div>
                        <span className="text-xs font-semibold uppercase text-blue-800">PI</span>
                        <p className="mt-1 font-medium text-gray-900">{piRaw || '—'}</p>
                        {slotDetail.resolved_pi ? (
                            <p className="text-xs text-gray-500">→ {slotDetail.resolved_pi}</p>
                        ) : null}
                    </div>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Cor interna (catálogo)
                    </label>
                    {colorsLoading ? (
                        <p className="text-sm text-gray-500">Carregando cores…</p>
                    ) : (
                        <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2">
                            {colors.map((c) => {
                                const hex = c.hex?.startsWith('#') ? c.hex : c.hex ? `#${c.hex}` : '#e5e7eb';
                                const selected = productColorId === c.id;
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setProductColorId(c.id)}
                                        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                                            selected
                                                ? 'bg-primary/10 ring-1 ring-primary'
                                                : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <span
                                            className="size-5 shrink-0 rounded-full border border-gray-300"
                                            style={{ backgroundColor: hex }}
                                        />
                                        <span className="font-medium text-gray-900">{c.name}</span>
                                    </button>
                                );
                            })}
                            {colors.length === 0 ? (
                                <p className="py-2 text-center text-sm text-gray-500">
                                    Nenhuma cor cadastrada nesta categoria.
                                </p>
                            ) : null}
                        </div>
                    )}
                </div>

                {(needsPoAlias || needsPiAlias) && productColorId ? (
                    <p className="text-xs text-gray-500">
                        {needsPoAlias && needsPiAlias
                            ? 'Serão criados 2 aliases (PO e PI) apontando para a mesma cor interna.'
                            : 'Será criado 1 alias para o nome que difere.'}
                    </p>
                ) : null}

                {error ? (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
                ) : null}

                {auditUser ? (
                    <p className="text-xs text-gray-500">
                        Cadastrado por {auditUser.name}
                        {auditAt
                            ? ` em ${new Date(auditAt).toLocaleString('pt-BR')}`
                            : ''}
                    </p>
                ) : null}
            </div>

            <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button
                    onClick={() => saveMut.mutate()}
                    disabled={!productColorId || saveMut.isPending}
                >
                    <PiPaletteBold className="mr-1 inline" />
                    {saveMut.isPending ? 'Salvando…' : 'Salvar vínculo'}
                </Button>
            </div>
        </V2Modal>
    );
}
